// src/components/shared/providers/ReactQueryProvider.tsx

'use client'

import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { useState, type ReactNode } from 'react'

/**
 * Provides a singleton React Query client for glossary search surfaces.
 *
 * This wrapper ensures a single QueryClient instance is created per browser session
 * and applies defaults that mirror our previous manual caching strategy. See
 * `docs/architecture/react-query-glossary.md` for a full comparison of the
 * behavioural guarantees carried over from the effect-driven glossary code.
 */
export function ReactQueryProvider({ children }: { children: ReactNode }): JSX.Element {
  const [queryClient] = useState(() => new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 1000 * 60 * 5, // keep data fresh for five minutes by default
        gcTime: 1000 * 60 * 30, // garbage collect cached results after thirty minutes
        refetchOnWindowFocus: false,
        retry: 1
      }
    }
  }))

  return <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
}
